import { cx } from '@twind/core';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, ComponentType } from 'react';

import { ToastPosition } from '../context/ToastContext';

export interface ToastProps {
  id: string;
  message: string;
  duration?: number;
  icon?: ComponentType<{ className?: string }>;
  position?: ToastPosition;
  onClose: (id: string) => void;
}

export function Toast({ id, message, duration = 5000, icon: Icon, onClose }: ToastProps) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -50, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={cx(
        'flex items-center gap-1 max-w-[329px] w-full min-h-[34px] bg-[#363636] text-white rounded-[32px] px-4 py-2'
      )}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      {Icon && (
        <div
          aria-hidden="true"
          className="flex-shrink-0"
        >
          <Icon className="w-4 h-4 text-white" />
        </div>
      )}

      <div className="flex-1 min-w-0 flex items-center">
        <span className="text-sm font-medium text-white truncate">{message}</span>
      </div>
    </motion.div>
  );
}

export interface ToastContainerProps {
  toasts: ToastProps[];
  position?: ToastPosition;
}

const positionStyles: Record<ToastPosition, string> = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
  'bottom-center': 'bottom-20 left-1/2 transform -translate-x-1/2',
};

export function ToastContainer({ toasts, position = 'bottom-center' }: ToastContainerProps) {
  return (
    <div
      className={cx('absolute z-[60] flex flex-col gap-2 pointer-events-none', positionStyles[position])}
      style={{
        // Ensure the container stays within the widget bounds
        maxWidth: 'calc(100% - 2rem)',
        maxHeight: 'calc(100% - 2rem)',
      }}
    >
      <AnimatePresence>
        {toasts.map((toast) => (
          <div
            className="pointer-events-auto"
            key={toast.id}
          >
            <Toast {...toast} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}
